/**
 * Pure helpers for the `/stores` page — labels, sorting, and the client-side register-form
 * validation. Kept separate from the React component so they're unit-testable in isolation
 * (matches the *-lib.ts convention). The server re-validates every write; these only gate the UI.
 */
import type { components } from '@/lib/api/types.gen';

export type StoreSummary = components['schemas']['StoreSummary'];

/** Human label for a store's auth grant. */
export function authTypeLabel(authType: StoreSummary['authType']): string {
  return authType === 'oauth' ? 'OAuth' : 'Client credentials';
}

/** Stable list order — by storeId slug, matching the server's sort. Does not mutate the input. */
export function sortStores(items: readonly StoreSummary[]): StoreSummary[] {
  return [...items].sort((a, b) => a.storeId.localeCompare(b.storeId));
}

const SLUG_RE = /^[a-z0-9-]+$/;
const SHOP_DOMAIN_RE = /\.myshopify\.com$/;
const API_VERSION_RE = /^\d{4}-\d{2}$/;
/** Anchored single-label form — mirrors the server's ShopifyShopDomainSchema (the OAuth begin gate). */
const CONNECT_DOMAIN_RE = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

/**
 * Client-side gate for the OAuth "Connect a store" form. Mirrors the server's anchored
 * ShopifyShopDomainSchema so the button disables before a doomed /stores/oauth/begin. The server
 * re-validates + normalizes; this only gates the UI.
 */
export function validateConnectDomain(domain: string): boolean {
  return CONNECT_DOMAIN_RE.test(domain.trim().toLowerCase());
}

/** The outcome the public OAuth callback encodes in its redirect back to /stores. */
export type InstallResult =
  | { kind: 'connected'; storeId: string }
  | { kind: 'cancelled'; storeId?: string }
  | { kind: 'error'; code: string; storeId?: string };

/**
 * Parse the install-result flags the callback appends to the /stores URL
 * (`?connected=<slug>` | `?install=cancelled&store=<slug>` | `?install=error&code=<c>&store=<slug>`).
 * Returns null when there's nothing to report. Pure — the page clears the query after showing it.
 */
export function parseInstallResult(search: string): InstallResult | null {
  const p = new URLSearchParams(search);
  const connected = p.get('connected');
  if (connected) return { kind: 'connected', storeId: connected };
  const install = p.get('install');
  if (install === 'cancelled') return { kind: 'cancelled', storeId: p.get('store') ?? undefined };
  if (install === 'error') {
    return {
      kind: 'error',
      code: p.get('code') ?? 'unknown',
      storeId: p.get('store') ?? undefined,
    };
  }
  return null;
}

/** apiVersion is optional; when present it must be a Shopify date version like `2026-07`. */
export function isValidApiVersion(v: string): boolean {
  const t = v.trim();
  return t === '' || API_VERSION_RE.test(t);
}

export type StoreDraft = {
  storeId: string;
  shopDomain: string;
  brandLabel: string;
  apiVersion: string;
};

/**
 * Client-side gate for the register form — mirrors StoreRegisterRequestSchema so the button
 * disables before a doomed request. The server (rest-api routes/stores.ts) is the real gate.
 */
export function validateStoreRegister(d: StoreDraft): boolean {
  const storeId = d.storeId.trim();
  const shopDomain = d.shopDomain.trim();
  return (
    SLUG_RE.test(storeId) &&
    storeId.length <= 64 &&
    SHOP_DOMAIN_RE.test(shopDomain) &&
    shopDomain.length <= 128 &&
    isValidApiVersion(d.apiVersion)
  );
}
